import { isMapStyleReady } from '../mapcore.js';
// Idrometri + nivometri DRPC — port 1:1 del blocco DRPC_EXTRA della v1
// (map-derived-layers.js, 2026-07-02): JSON same-origin da ws-drpc-extra-export,
// circleMarker r8 (tolerance v1: "i pallini radius 6 erano quasi incliccabili"),
// tooltip "nome: valore unità", popup .ws-water-popup con badge + SPARKLINE
// degli ultimi ~15 giorni dalla serie del JSON (drpcExtraPopup/drpcExtraSparkline).
const DEFS = {
    idrometri: { url: '/assets/data/drpc/idrometri.json', color: '#2f9be0', fill: '#2f9be0', label: 'Idrometro' },
    neve: { url: '/assets/data/drpc/neve.json', color: '#8ecbff', fill: '#eaf6ff', label: 'Neve' },
};

const state = { map: null, enabled: {}, seq: {}, popup: null, hover: null };

function esc(s) { return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

// Mini-grafico degli ultimi giorni (port ESATTO di drpcExtraSparkline v1).
function drpcExtraSparkline(series, color) {
    const pts = (series || []).map(p => [String(p[0]), Number(p[1])]).filter(p => Number.isFinite(p[1]));
    if (pts.length < 2) return '';
    const w = 206, h = 46, pad = 4;
    const vals = pts.map(p => p[1]);
    const min = Math.min(...vals), max = Math.max(...vals);
    const span = (max - min) || 1;
    const step = (w - pad * 2) / (pts.length - 1);
    const poly = pts.map((p, i) =>
        `${(pad + i * step).toFixed(1)},${(h - pad - ((p[1] - min) / span) * (h - pad * 2)).toFixed(1)}`).join(' ');
    const fmt = v => esc(Number.isInteger(v) ? v : v.toFixed(2));
    const day = d => esc(d.slice(8, 10) + '/' + d.slice(5, 7));
    return `<div class="ws-idro-spark"><svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" aria-hidden="true">`
        + `<polyline points="${poly}" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></polyline>`
        + `</svg><div><span>${day(pts[0][0])}</span><span>min ${fmt(min)} · max ${fmt(max)}</span><span>${day(pts[pts.length - 1][0])}</span></div></div>`;
}

// Popup con badge + sparkline per elemento (port ESATTO di drpcExtraPopup v1).
function popupHtml(s, kind) {
    const cfg = DEFS[kind] || DEFS.idrometri;
    const rows = (s.elements || []).map(e => {
        const val = e.latest == null ? '–' : `${e.latest} ${e.unit || ''}`;
        return `<p><strong>${esc(e.name)}: ${esc(val)}</strong></p>`
            + drpcExtraSparkline(e.series, cfg.color)
            + (e.latest_date ? `<small>Ultimo dato: ${esc(e.latest_date)} · Fonte: DRPC</small>` : '');
    }).join('');
    return `<article class="ws-water-popup"><span class="ws-water-popup__badge">${esc(cfg.label)}</span>`
        + `<h3>${esc(s.nome)}${s.prov ? ' · ' + esc(s.prov) : ''}</h3>${rows}</article>`;
}

function srcId(kind) { return 'ws-drpcx-' + kind; }

// Click tolerance (v1 renderer tolerance): il click viene cercato in un
// riquadro di ±6px attorno al punto, non solo sul pixel esatto del cerchio.
const CLICK_TOL_PX = 6;

function ensureLayer(kind) {
    const map = state.map;
    const id = srcId(kind);
    if (map.getSource(id)) return true;
    if (!isMapStyleReady(map)) { map.once('idle', () => { if (state.enabled[kind]) activate(kind); }); return false; }
    const d = DEFS[kind];
    map.addSource(id, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    // v1 (fix cliccabilità): radius 8, bordo 2.5, fillOpacity .85
    map.addLayer({
        id, type: 'circle', source: id,
        paint: {
            'circle-radius': 8,
            'circle-color': d.fill,
            'circle-opacity': 0.85,
            'circle-stroke-color': d.color,
            'circle-stroke-width': 2.5,
        },
    }, map.getLayer('ws-stations-symbols') ? 'ws-stations-symbols' : undefined);

    if (!state.hover) {
        state.hover = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 10, className: 'wsv2-tooltip-shell ws-derived-tooltip-shell', maxWidth: '260px' });
    }
    map.on('mousemove', id, (e) => {
        const f = e.features?.[0];
        if (!f) return;
        map.getCanvas().style.cursor = 'pointer';
        state.hover.setLngLat(f.geometry.coordinates).setHTML(`<div class="ws-derived-tooltip">${f.properties.tt}</div>`).addTo(map);
    });
    map.on('mouseleave', id, () => { map.getCanvas().style.cursor = ''; state.hover.remove(); });
    return true;
}

// Click con tolleranza sul singolo layer attivo (agganciato UNA volta).
function wireClick() {
    if (state._clickWired) return;
    state._clickWired = true;
    state.map.on('click', (e) => {
        const map = state.map;
        const lyrs = Object.keys(DEFS).filter(k => state.enabled[k] && map.getLayer(srcId(k)));
        if (!lyrs.length) return;
        const p = e.point;
        const feats = map.queryRenderedFeatures(
            [[p.x - CLICK_TOL_PX, p.y - CLICK_TOL_PX], [p.x + CLICK_TOL_PX, p.y + CLICK_TOL_PX]],
            { layers: lyrs.map(srcId) });
        const f = feats && feats[0];
        if (!f) return;
        state.hover?.remove();
        state.popup?.remove();
        state.popup = new maplibregl.Popup({ className: 'wsv2-tooltip-shell ws-water-popup-shell', maxWidth: '280px', offset: 10 })
            .setLngLat(f.geometry.coordinates).setHTML(f.properties.pp).addTo(map);
    });
}

async function activate(kind) {
    const seq = (state.seq[kind] = (state.seq[kind] || 0) + 1);
    if (!ensureLayer(kind)) return;
    wireClick();
    try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 12000);
        let data;
        try {
            const r = await fetch(DEFS[kind].url, { cache: 'no-store', credentials: 'omit', signal: controller.signal });
            if (!r.ok) throw new Error('HTTP ' + r.status);
            data = await r.json();
        } finally { clearTimeout(timer); }
        if (seq !== state.seq[kind] || !state.enabled[kind]) return;
        const feats = [];
        for (const s of (Array.isArray(data.stations) ? data.stations : [])) {
            const lat = parseFloat(s.lat), lon = parseFloat(s.lon);
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
            const prim = s.elements && s.elements[0];
            feats.push({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [lon, lat] },
                properties: {
                    tt: esc(prim ? `${s.nome}: ${prim.latest == null ? '–' : prim.latest + ' ' + (prim.unit || '')}` : s.nome),
                    pp: popupHtml(s, kind),
                },
            });
        }
        state.map.getSource(srcId(kind))?.setData({ type: 'FeatureCollection', features: feats });
    } catch (e) {
        console.warn('[wsv2] DRPC ' + kind + ' non disponibile:', e);
    }
}

export function initDrpcExtra(map) { state.map = map; }

export function setDrpcExtraEnabled(kind, on) {
    if (!DEFS[kind]) return;
    state.enabled[kind] = !!on;
    if (on) activate(kind);
    else {
        state.seq[kind] = (state.seq[kind] || 0) + 1;
        state.map.getSource(srcId(kind))?.setData({ type: 'FeatureCollection', features: [] });
        state.popup?.remove();
    }
}
